import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft } from 'lucide-react';
import KeyboardDismissView from './KeyboardDismissView';
import { useTheme } from '../../context/ThemeContext';
import useModalBack from '../../hooks/useModalBack';

export default function Modal({ isOpen, onClose, title, children, size = 'md', fullScreenMobile = true, disableEscClose = false }) {
  const { isModernUI } = useTheme();
  // Store the initial viewport height to prevent resize on keyboard open
  const [modalHeight, setModalHeight] = useState(null);

  // Back button handling — centralized via useModalBack (prevents cascading closes)
  useModalBack(isOpen, onClose, `Modal:${title}`);

  // Close on escape key
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && !disableEscClose) {
        onClose();
      }
    },
    [onClose, disableEscClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Capture the viewport height when modal opens (before keyboard appears)
      setModalHeight(window.innerHeight);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  // Full screen on mobile — covers bottom nav completely
  const mobileModalHeight = modalHeight ? `${modalHeight}px` : '100dvh';

  // Full-screen mobile layout
  if (fullScreenMobile) {
    return createPortal(
      <>
        {/* Mobile: Full-screen page - leaves space for bottom nav */}
        {/* Use fixed height instead of bottom positioning to prevent resize on keyboard open */}
        <div
          className={`xl:hidden fixed top-0 left-0 right-0 z-[60] flex flex-col safe-area-top ${isModernUI ? 'animate-slide-up' : ''}`}
          style={{
            height: mobileModalHeight,
            backgroundColor: 'var(--page-bg, #f8fafc)'
          }}
          data-no-swipe="true"
          data-modal="true"
        >
          {/* Mobile Header */}
          <div
            className={`flex items-center sticky top-0 z-10 flex-shrink-0 ${
              isModernUI ? 'gap-2 px-3 py-2.5 border-b border-white/10' : 'gap-3 px-4 py-3.5 border-b border-surface-200 bg-white'
            }`}
            style={isModernUI ? {
              background: `linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 85%, black))`,
            } : {}}
          >
            <button
              onClick={onClose}
              className={isModernUI
                ? 'p-1.5 -ml-0.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300 active:scale-90'
                : 'p-2 -ml-1 rounded-xl text-surface-600 hover:text-surface-900 hover:bg-surface-100 transition-all'
              }
              style={isModernUI ? { color: 'var(--accent-text)' } : {}}
            >
              <ArrowLeft className={isModernUI ? "w-4 h-4" : "w-5 h-5"} />
            </button>
            <h2
              className={isModernUI ? 'text-sm font-bold flex-1' : 'text-lg font-bold flex-1 text-surface-900'}
              style={isModernUI ? { color: 'var(--accent-text)' } : {}}
            >
              {title}
            </h2>
          </div>

          {/* Mobile Content - Scrollable with safe area padding at bottom */}
          <KeyboardDismissView className={isModernUI ? "flex-1 overflow-y-auto px-3 py-4 pb-6 safe-area-bottom" : "flex-1 overflow-y-auto px-4 py-5 pb-8 safe-area-bottom"}>
            {children}
          </KeyboardDismissView>
        </div>

        {/* Desktop: Traditional modal */}
        <div className="hidden xl:flex modal-overlay" onClick={onClose} data-no-swipe="true" data-modal="true">
          <div
            className={`modal-content ${sizeClasses[size]} ${isModernUI ? 'animate-scale-in' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            style={isModernUI ? {
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
            } : {}}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
              <h2 className="text-lg font-bold text-surface-900">{title}</h2>
              <button
                onClick={onClose}
                className={`p-2 -mr-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-xl transition-all ${
                  isModernUI ? 'duration-300 active:scale-90' : ''
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5">{children}</div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Traditional modal for both mobile and desktop (when fullScreenMobile is false)
  return createPortal(
    <div className="modal-overlay" onClick={onClose} data-no-swipe="true" data-modal="true">
      <div
        className={`modal-content ${sizeClasses[size]} ${isModernUI ? 'animate-scale-in' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        style={isModernUI ? {
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
        } : {}}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
          <h2 className="text-lg font-bold text-surface-900">{title}</h2>
          <button
            onClick={onClose}
            className={`p-2 -mr-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-xl transition-all ${
              isModernUI ? 'duration-300 active:scale-90' : ''
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}
